import { Button, Dialog, Icon, ProgressBar, Tooltip } from '@blueprintjs/core';
import AddressDetailTable from 'Components/Generic/Address/AddressDetailTable';
import { StatusSelectButton } from 'Components/Generic/BOSSelect/StatusSelect';
import {
  showSaveSuccessToast,
  showSuccessToast,
  showToast,
} from 'Components/Generic/BOSToasterUtil';
import DuplicateButton from 'Components/Generic/Buttons/DuplicateButton';
import DetailPanel from 'Components/Generic/DetailPanel';
import NotesListPanel from 'Components/Generic/Notes/NotesListPanel';
import { SITE_PAGES } from 'Config';
import { useAlertContext } from 'Data/context/Alerts';
import { ShippingStateProvider } from 'Data/context/Shipping';
import { useViewportSizer } from 'Data/context/ViewportSizer';
import { user_org_roles, user_roles } from 'Data/Enum';
import { LineItemData } from 'Data/LineItemData';
import RequestApprovalRequestsData from 'Data/RequestApprovalRequestsData';
import RequestData from 'Data/RequestData';
import { TransactionData } from 'Data/TransactionData';
import DataManager from 'Lib/DataManager';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import VendorInformationPanel from 'Pages/Admin/Vendors/VendorInformationPanel';
import {
  getStatusObject,
  NOTE_UPDATE_ACTIONS,
  REQUEST_STATUS_KEYS,
} from 'Pages/Requests/Enum';
import { LINE_ITEM_STATUS } from 'Pages/Transactions/Enum';
import TransactionDetails from 'Pages/Transactions/TransactionDetails';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import styles from 'Styles/styles.module.css';
import { isUserAuthorized } from 'Util';
import { generatePDFforRequest } from 'Util/pdfUtil/transactionPrintHelper';
import LineItemsPanel from '../RequestDetails/LineItemsPanel';
import RequestDocumentsPanel from '../RequestDetails/RequestDocumentsPanel';
import RequestInformationPanel from '../RequestDetails/RequestInformationPanel';
import RequestWorkflowPanel from '../RequestDetails/RequestWorkflowPanel';
import RequestLanding from '../RequestLanding';
import { RequestStatusButton } from './RequestStatus';
import SplitRequestPanel from './SplitRequestPanel';

let DM = new DataManager();

const RequestDetails = ({ _isNew, requestObject, params }) => {
  const { narrowScreen } = useViewportSizer();
  const location = useLocation();
  const navigate = useNavigate();

  const [request, setRequest, requestRef] = useStateWithRef(
    requestObject
      ? RequestData.getRequestDetails(requestObject, true)
      : undefined,
  );
  const [isNew, setIsNew, isNewRef] = useStateWithRef(false);
  const [isExporting, setIsExporting] = useState(false);

  const [splitWindowOpen, setSplitWindowOpen] = useState(false);

  const { openConfirmationDialog } = useAlertContext();

  let requestIsEditable =
    isNew ||
    request?.status === REQUEST_STATUS_KEYS.NEW ||
    request?.status === REQUEST_STATUS_KEYS.REJECTED ||
    isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR, user_roles.TRANSACTOR],
    });

  let breadcrumbs = request
    ? [
        !location?.pathname?.includes('all') &&
        isUserAuthorized(SITE_PAGES.REQUEST_ORDERS.requiredAuth)
          ? SITE_PAGES.REQUEST_ORDERS
          : SITE_PAGES.ALL_ORDERS,
        {
          icon: 'document',
          text: params?.id || (isNew && 'New'),
        },
      ]
    : [];

  useEffect(() => {
    DM.add(
      RequestData.registerListener('REQUEST_LOADED', onRequestLoaded),
      RequestData,
    );
    DM.add(
      RequestData.registerListener('REQUEST_CREATED', onRequestCreated),
      RequestData,
    );
    DM.add(
      RequestData.registerListener(
        'REQUEST_STATUS_UPDATED',
        onRequestStatusUpdated,
      ),
      RequestData,
    );
    DM.add(
      RequestData.registerListener('RECORD_UPDATED', onRequestUpdated),
      RequestData,
    );
    DM.add(
      RequestData.registerListener(
        'APPROVAL_SKIPPED',
        onRequestApprovalStatusSkipped,
      ),
      RequestData,
    );
    DM.add(
      RequestData.registerListener('REQUEST_CLAIMED', onRequestClaimed),
      RequestData,
    );
    DM.add(
      LineItemData.registerListener(
        'LINE_ITEMS_MARKED_AS_PURCHASED',
        refreshRequest,
      ),
      LineItemData,
    );
    DM.add(
      RequestApprovalRequestsData.registerListener(
        'RECORDS_UPDATED',
        onRequestApprovalCreated,
      ),
      RequestApprovalRequestsData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (_isNew) {
      setIsNew(true);
      !requestObject && setRequest(RequestData.getBlankRequest());
    }
  }, [_isNew]);

  useEffect(() => {
    params?.id && !isNaN(params.id) && RequestData.getRequest(params.id);
    if (params?.id === 'null') {
      setIsNew(true);
    }
  }, [params]);

  useEffect(() => {
    setIsExporting(false);
  }, [request]);

  useEffect(() => {
    if (request?.line_items?.length && request?.transactions?.length) {
      if (
        request.status !== REQUEST_STATUS_KEYS.RECEIVED &&
        request.line_items.every(
          (lineItem) => lineItem.status === LINE_ITEM_STATUS.RECEIVED.key,
        )
      ) {
        RequestData.updateStatus(request, REQUEST_STATUS_KEYS.RECEIVED);
      } else if (
        ![REQUEST_STATUS_KEYS.PURCHASED, REQUEST_STATUS_KEYS.RECEIVED].includes(
          request.status,
        ) &&
        request.line_items.every(
          (lineItem) =>
            lineItem.status === LINE_ITEM_STATUS.PURCHASED.key ||
            lineItem.status === LINE_ITEM_STATUS.RECEIVED.key,
        )
      ) {
        RequestData.updateStatus(request, REQUEST_STATUS_KEYS.PURCHASED);
      }
    }
  }, [request?.line_items]);

  const onRequestLoaded = (payload) => {
    if (payload) {
      setRequest(payload);
    }
  };

  const refreshRequest = () => {
    requestRef.current.id && RequestData.getRequest(requestRef.current.id);
  };

  const onRequestCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Request Created');
      RequestData.getRequest(payload.id);
    }
  };

  const onRequestUpdated = (payload) => {
    if (payload) {
      RequestData.getRequest(payload.id);
    }
  };

  const onRequestNotesUpdated = (payload) => {
    if (payload.action !== NOTE_UPDATE_ACTIONS.DELETED) {
      RequestData.sendUpdateEmail(request, payload);
    }
    refreshRequest();
  };

  const markTransactionPurchased = (transaction) => {
    let transactionLines = request.line_items.filter(
      (line) => line.assigned_transaction_id === transaction.id,
    );
    TransactionData.markAsPurchased(transaction, transactionLines);
  };

  const onStatusButtonClicked = (currentStatus, options = {}) => {
    switch (currentStatus) {
      case REQUEST_STATUS_KEYS.NEW:
        RequestData.moveRequestStatusForward(request);
        break;
      case REQUEST_STATUS_KEYS.SUBMITTED:
        if (options.approved) {
          RequestData.moveRequestStatusForward(request);
        } else {
          RequestData.updateStatus(request, REQUEST_STATUS_KEYS.REJECTED);
        }
        break;
      case REQUEST_STATUS_KEYS.APPROVED:
        if (options.split) {
          if (request.line_items?.length === 2) {
            let transactions = [
              { lines: new Set([request.line_items[0]]) },
              { lines: new Set([request.line_items[1]]) },
            ];
            confirmSplitTransaction(transactions);
          } else {
            setSplitWindowOpen(true);
          }
        } else {
          TransactionData.createTransactionFromRequest({
            request,
          });
        }
        break;
      case REQUEST_STATUS_KEYS.TRANSACTION_CREATED:
        request.transactions.forEach((trans) =>
          markTransactionPurchased(trans),
        );
        RequestData.moveRequestStatusForward(request);
        break;
      case REQUEST_STATUS_KEYS.PURCHASED:
        if (request.line_items.some((lineItem) => !lineItem.date_received)) {
          openConfirmationDialog({
            onConfirm: () =>
              RequestData.updateStatus(
                requestRef.current,
                REQUEST_STATUS_KEYS.RECEIVED,
              ),
            dialogTitle: 'Mark request received?',
            dialogMessage:
              'Not all line items have been marked as received.  Are you sure you want to mark the request as received?',
          });
        } else {
          RequestData.updateStatus(request, REQUEST_STATUS_KEYS.RECEIVED);
        }
        break;
      case REQUEST_STATUS_KEYS.RECEIVED:
        return;
    }
  };

  const onRequestStatusUpdated = (updatedRequest) => {
    let newStatus = updatedRequest.status;
    let message = 'Request status updated';

    switch (newStatus) {
      case REQUEST_STATUS_KEYS.NEW:
        message = 'Request saved';
        break;
      // case REQUEST_STATUS_KEYS.SUBMITTED:
      //   message = 'Request submitted';
      //   break;
      case REQUEST_STATUS_KEYS.APPROVED:
        message = 'Request approved';
        break;
      case REQUEST_STATUS_KEYS.REJECTED:
        message = 'Request rejected';
        break;
      case REQUEST_STATUS_KEYS.TRANSACTION_CREATED:
        message = 'Transaction created';
        break;
      case REQUEST_STATUS_KEYS.PURCHASED:
        message = 'Request marked as purchased';
        break;
      case REQUEST_STATUS_KEYS.RECEIVED:
        message = 'Request marked received';
        break;
    }

    showSuccessToast(message);
    refreshRequest();
  };

  const onRequestClaimed = () => {
    showSuccessToast('Request claimed by you');
    refreshRequest();
  };

  const onRequestApprovalCreated = (rars) => {
    showSuccessToast('Request submitted');

    if (rars?.length) {
      RequestApprovalRequestsData.sendApprovalEmails(requestRef.current, rars)
        .then((res) => {})
        .catch((err) => {
          console.log('error sending approval request emails');
          console.error(err);
        })
        .finally(() => {
          rars[0] &&
            isNewRef.current &&
            navigate(
              `${SITE_PAGES.ORDER_DETAIL.to}/${rars[0].parent_request_id}`,
            );
        });
    }
  };

  const onRequestApprovalStatusSkipped = (savedRequest) => {
    showSuccessToast('Request submitted');
    savedRequest &&
      isNewRef.current &&
      navigate(`${SITE_PAGES.ORDER_DETAIL.to}/${savedRequest.id}`);
    savedRequest && !isNewRef.current && refreshRequest();
  };

  let showStatusWorkflowButton =
    request?.id &&
    request.status !== REQUEST_STATUS_KEYS.RECEIVED &&
    ((request.status === REQUEST_STATUS_KEYS.SUBMITTED &&
      isUserAuthorized({
        roles: [
          user_roles.ADMINISTRATIVE_APPROVER,
          [request.parent_organization_id, user_org_roles.REQUEST_APPROVER.key],
        ],
      })) ||
      (request.status === REQUEST_STATUS_KEYS.APPROVED &&
        !request.transactions?.length &&
        isUserAuthorized({
          roles: [user_roles.TRANSACTOR, user_roles.BUYER],
        })) ||
      [
        REQUEST_STATUS_KEYS.NEW,
        REQUEST_STATUS_KEYS.PURCHASED,
        REQUEST_STATUS_KEYS.REJECTED,
      ].includes(request.status));

  const confirmSplitTransaction = (transactionsToCreate) => {
    let requestShippingItem = request.line_items?.length
      ? request.line_items[0].shipping
      : {};
    let { id, delivery_address_id, shipping_speed, amount } =
      requestShippingItem;

    transactionsToCreate.forEach((transaction, index) => {
      TransactionData.createTransactionFromRequest({
        request,
        shippingItem: {
          delivery_address_id,
          shipping_speed,
          ...(!index && { amount }),
          ...(!index && id && { id }),
        },
        lineItems: [...transaction.lines],
      });
    });
    setSplitWindowOpen(false);
  };

  const exportPdfClicked = (download = false) => {
    if (!request?.id) {
      return;
    }
    setIsExporting(true);
    let callback = () => setIsExporting(false);
    let errorhandler = (e) => {
      console.error(`Error generating PDF for request: ${request.id}`);
      console.error(e);
      setIsExporting(false);
      showToast(`Error generating PDF: ${e.message}`, 'warning', 'issue');
    };

    generatePDFforRequest(request, download, callback, errorhandler);
  };

  let openReportButton = request?.id && (
    <Tooltip
      minimal
      content={'Click to open request as PDF'}
      placement={'auto'}
      shouldReturnFocusOnClose={false}>
      <Button
        minimal
        className={styles.primary_button_minimal}
        onClick={() => exportPdfClicked(false)}
        icon={'document-open'}
        disabled={isExporting}>
        {narrowScreen ? '' : 'Open as PDF'}
      </Button>
    </Tooltip>
  );

  const duplicateClickHandler = () => {
    navigate(SITE_PAGES.NEW_ORDER.to, { state: { request } });
  };

  let duplicateButton = !isNew && request?.id && (
    <DuplicateButton
      duplicateClickHandler={duplicateClickHandler}
      buttonText={narrowScreen ? '' : 'Duplicate Request'}
    />
  );

  let titleButtons = (
    <>
      {openReportButton}
      {duplicateButton}
    </>
  );

  let noteIdList = request?.request_notes?.map((note) => note.id) || [];

  let pageContent = request && (
    <ShippingStateProvider request={request} editable={requestIsEditable}>
      <div className={styles.panel_flex_box}>
        <Dialog
          isOpen={splitWindowOpen}
          canOutsideClickClose
          canEscapeKeyClose
          onClose={() => setSplitWindowOpen(false)}
          title={'Split Request'}>
          <SplitRequestPanel
            request={request}
            onCancel={() => setSplitWindowOpen(false)}
            onConfirm={confirmSplitTransaction}
          />
        </Dialog>
        <RequestInformationPanel
          editable={requestIsEditable}
          requestObject={request}
          isNew={isNew}
        />
        {!isNew && (
          <VendorInformationPanel
            vendorObject={request.vendor}
            hideFax
            collapsible={false}
          />
        )}
        {!isNew && request.address && (
          <DetailPanel title='Delivery Instructions'>
            <AddressDetailTable addressObject={request.address} />
          </DetailPanel>
        )}
        {!isNew && request?.status !== REQUEST_STATUS_KEYS.NEW && (
          <RequestWorkflowPanel
            request={request}
            editable={
              requestIsEditable &&
              isUserAuthorized({
                roles: [
                  user_roles.ADMINISTRATOR,
                  user_roles.BUYER,
                  user_roles.TRANSACTOR,
                ],
              })
            }
            showAlternateFooter={
              request.status === REQUEST_STATUS_KEYS.SUBMITTED
            }
            alternateButtonFooter={
              isUserAuthorized({
                roles: [
                  user_roles.ADMINISTRATIVE_APPROVER,
                  [
                    request.parent_organization_id,
                    user_org_roles.REQUEST_APPROVER.key,
                  ],
                ],
              }) ? (
                <RequestStatusButton
                  request={request}
                  onStatusButtonClicked={onStatusButtonClicked}
                  fillWidth
                />
              ) : (
                <StatusSelectButton
                  statusObject={getStatusObject(request.status)}
                  disabled
                />
              )
            }
          />
        )}
        <RequestDocumentsPanel
          request={request}
          onDocumentsUpdated={refreshRequest}
          disabled={isNew && !request.id}
          canAddDoc={!!request.id}
          editable={
            request.documents?.length > 0 &&
            isUserAuthorized({
              roles: [
                user_roles.ADMINISTRATOR,
                user_roles.BUYER,
                user_roles.TRANSACTOR,
              ],
            })
          }
        />
        <NotesListPanel
          notesIdList={noteIdList}
          notesTableName='request_notes'
          dataIdentifier='R_NOTES'
          disabled={isNew && !request.id}
          canAddNotes={!!request.id}
          editable={requestIsEditable}
          hideToggle
          parentEditState={requestIsEditable || !!request.id}
          newNoteFieldValues={{ request_id: request.id }}
          onNotesUpdated={onRequestNotesUpdated}
          collapsible={false}
        />
        {(!request.transactions ||
          !request.transactions?.length ||
          request.line_items.some((li) => !li.assigned_transaction_id)) && (
          <div className={styles.wide_panel}>
            <LineItemsPanel
              title={
                request.transactions?.length
                  ? 'Line Items (not yet assigned to a transaction)'
                  : 'Line Items'
              }
              request={request}
              editable={
                isNew ||
                request.status === REQUEST_STATUS_KEYS.NEW ||
                isUserAuthorized({
                  roles: [
                    user_roles.ADMINISTRATOR,
                    user_roles.BUYER,
                    user_roles.TRANSACTOR,
                  ],
                })
              }
              disabled={isNew && !request.id}
              onUpdateLineItems={refreshRequest}
              isNew={isNew}
            />
          </div>
        )}
        <div
          className={styles.full_width_panel}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {request.transactions &&
            request.transactions
              .sort((a, b) => a.id - b.id)
              .map((trans, index) => {
                return (
                  <DetailPanel
                    key={index}
                    collapsible
                    alternativeBackgroundColor
                    editable={requestIsEditable}
                    title={
                      <ConditionalWrapper
                        wrapper={(children) => (
                          <Link
                            className={styles.bos_link}
                            to={`${SITE_PAGES.TRANSACTION_DETAIL.to}/${trans.id}`}>
                            {children}
                            <Icon icon={'share'} />
                          </Link>
                        )}
                        condition={isUserAuthorized(
                          SITE_PAGES.TRANSACTION_DETAIL.requiredAuth,
                        )}>
                        {`Transaction ${trans.id}`}{' '}
                      </ConditionalWrapper>
                    }>
                    <TransactionDetails
                      transactionId={trans.id}
                      onTransactionUpdated={refreshRequest}
                      showEssentialsOnly
                    />
                  </DetailPanel>
                );
              })}
        </div>
        {showStatusWorkflowButton && (
          <RequestStatusButton
            request={request}
            onStatusButtonClicked={onStatusButtonClicked}
          />
        )}
      </div>
    </ShippingStateProvider>
  );

  return (
    <RequestLanding
      breadcrumbs={breadcrumbs}
      title={isNew ? 'New Order Request' : `Order ${params?.id || request?.id}`}
      titleButton={titleButtons}>
      {isExporting && <ProgressBar />}
      {pageContent}
    </RequestLanding>
  );
};

RequestDetails.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string,
  }),
  _isNew: PropTypes.bool,
  requestObject: PropTypes.object,
};

export default RequestDetails;
